import { Controller } from "stimulus"
import gsap from "gsap";

export default class extends Controller {
  static targets = ["background", "item"]

  connect() {
    // Initialize the background position
    this.initializeBackground()

    // Set up Turbo event listeners for navigation
    document.addEventListener('turbo:before-stream-render', this.handleTurboStreamRender.bind(this))

    // Add click handlers to items if not using Turbo
    this.itemTargets.forEach(item => {
      item.addEventListener('click', (e) => {
        this.setActiveItem({ currentTarget: item })
      })
    })

    // Add resize handler with debounce
    this.resizeHandler = this.debounce(() => {
      const activeItem = this.itemTargets.find(item => item.dataset.active === "true")
      if (activeItem) {
        this.moveBackground(activeItem, false) // false means no animation
      }
    }, 250) // 250ms debounce

    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    window.removeEventListener('resize', this.resizeHandler)
    document.removeEventListener('turbo:before-stream-render', this.handleTurboStreamRender)
  }

  createBackgroundElement() {
    // Check if background already exists
    if (this.hasBackgroundTarget) return

    // Create a moving background element and insert it into the DOM
    const background = document.createElement('div')
    background.classList.add('absolute', 'rounded-full', 'bg-gray-600', 'z-0', 'hidden')
    background.setAttribute('data-rounded-nav-target', 'background')
    background.style.border = '1px solid rgba(165, 169, 170, 0.25)'

    // Insert at the beginning of the controller element
    this.element.prepend(background)
  }

  initializeBackground() {
    const activeItem = this.itemTargets.find(item => item.dataset.active === "true")
    if (activeItem) {
      // Position the background without animation
      this.moveBackground(activeItem, false)

      // Make sure the background has the correct initial opacity
      gsap.set(this.backgroundTarget, { opacity: 0 })

      // Wait a tiny bit to ensure positioning is applied, then show and fade in
      setTimeout(() => {
        // Explicitly remove the Tailwind hidden class and set display to block
        this.backgroundTarget.classList.remove('hidden')
        this.backgroundTarget.style.display = 'block'

        gsap.to(this.backgroundTarget, {
          opacity: 1,
          duration: 0.2,
          ease: "power1.inOut"
        })
      }, 50)
    }
  }

  handleTurboStreamRender(event) {
    // Check if this stream affects our navbar tabs
    if (event.detail?.newStream?.templateElement?.content?.querySelector('[data-rounded-nav-target="item"]')) {
      // Get the active item after the stream renders
      setTimeout(() => {
        const activeItem = this.itemTargets.find(item => item.dataset.active === "true")
        if (activeItem) {
          this.moveBackground(activeItem, true)
        }
      }, 0)
    }
  }

  // This can be called from the navigation items
  setActiveItem(event) {
    const clickedItem = event.currentTarget

    // Update active states manually if needed
    this.itemTargets.forEach(item => {
      item.dataset.active = (item === clickedItem) ? "true" : "false"
    })

    // Animate background
    this.moveBackground(clickedItem, true)
  }

  moveBackground(targetItem, animate = true) {
    const rect = targetItem.getBoundingClientRect()
    const navRect = this.element.getBoundingClientRect()
    const leftOffset = rect.left - navRect.left

    // Add some padding to the height to ensure it looks good on all screen sizes
    const heightPadding = 8 // 4px padding on top and bottom
    const height = rect.height + heightPadding

    // Get values for the animation - ensure we have proper dimensions
    const props = {
      x: leftOffset,
      width: rect.width,
      height: height,
      top: `${-heightPadding/2}px`  // Center the background vertically
    }

    if (animate) {
      gsap.to(this.backgroundTarget, {
        ...props,
        duration: 0.3,
        ease: "power2.out"
      })
    } else {
      // Apply styles directly in addition to gsap to ensure they take effect
      Object.assign(this.backgroundTarget.style, {
        width: `${rect.width}px`,
        height: `${height}px`,
        top: `${-heightPadding/2}px`
      })

      gsap.set(this.backgroundTarget, props)
    }
  }

  // Utility function to debounce resize events
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}
